import React, { useRef, useState, useEffect } from 'react';
import { MERGE_TAG_DEFS } from './mergeTags';

interface Props {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * A textarea that:
 *  1. Shows an autocomplete dropdown when the user types `[`
 *  2. Renders the value as highlighted tokens in a read-layer overlay
 */
export function FooterVariableTextarea({ value, onChange, rows = 2, className = '', style }: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showSuggest, setShowSuggest] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [caretStart, setCaretStart] = useState(0);

  const filteredTags = MERGE_TAG_DEFS.filter(t =>
    t.tag.toLowerCase().includes(filterText.toLowerCase()) ||
    t.label.toLowerCase().includes(filterText.toLowerCase())
  );

  const handleKeyUp = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    const pos = ta.selectionStart;
    const text = ta.value;
    // Find the last `[` before caret
    const before = text.slice(0, pos);
    const bracketIdx = before.lastIndexOf('[');
    if (bracketIdx !== -1 && !before.slice(bracketIdx).includes(']')) {
      setCaretStart(bracketIdx);
      setFilterText(before.slice(bracketIdx + 1));
      setShowSuggest(true);
    } else {
      setShowSuggest(false);
    }
  };

  const insertTag = (tag: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const pos = ta.selectionStart;
    const text = ta.value;
    const before = text.slice(0, caretStart);
    const after = text.slice(pos);
    const newVal = before + tag + after;
    onChange(newVal);
    setShowSuggest(false);
    setTimeout(() => {
      ta.focus();
      const newPos = caretStart + tag.length;
      ta.setSelectionRange(newPos, newPos);
    }, 10);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (textareaRef.current && !textareaRef.current.contains(e.target as Node)) {
        setShowSuggest(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative w-full" style={style}>
      {/* Background layer for highlights */}
      <div
        className={`absolute inset-0 pointer-events-none whitespace-pre-wrap break-words ${className}`}
        style={{ 
          color: 'transparent', 
          padding: 0, 
          margin: 0, 
          border: 'none', 
          overflow: 'hidden',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          fontWeight: 'inherit',
          lineHeight: style?.lineHeight || '1.5',
          letterSpacing: 'inherit',
          textAlign: 'inherit',
          boxSizing: 'border-box'
        }}
        aria-hidden="true"
      >
        <VariableHighlightText text={value} style={{ margin: 0, padding: 0, lineHeight: style?.lineHeight || '1.5' }} />
      </div>
      
      {/* Foreground textarea */}
      <textarea
        ref={textareaRef}
        value={value}
        rows={rows}
        onChange={e => onChange(e.target.value)}
        onKeyUp={handleKeyUp}
        onClick={handleKeyUp}
        className={`relative z-10 border-0 focus:outline-none focus:ring-0 resize-none w-full overflow-hidden bg-transparent ${className}`}
        style={{ 
          color: 'inherit', 
          padding: 0, 
          margin: 0,
          fontFamily: 'inherit',
          fontSize: 'inherit',
          fontWeight: 'inherit',
          lineHeight: style?.lineHeight || '1.5',
          letterSpacing: 'inherit',
          textAlign: 'inherit',
          boxSizing: 'border-box',
          display: 'block'
        }}
      />
      
      {showSuggest && filteredTags.length > 0 && (
        <div className="absolute left-0 top-full z-50 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-lg shadow-xl w-64 max-h-48 overflow-y-auto">
          {filteredTags.map(t => (
            <button
              key={t.tag}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-zinc-800 text-xs flex items-center justify-between gap-2"
              onMouseDown={e => { e.preventDefault(); insertTag(t.tag); }}
            >
              <span className="font-medium text-gray-800 dark:text-gray-200">{t.label}</span>
              <span className="text-gray-400 font-mono text-[10px] shrink-0">{t.tag}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function VariableHighlightText({ text, style }: { text: string; style?: React.CSSProperties }) {
  const parts = text.split(/(\[[A-Za-z]+\.[A-Za-z]+\])/g);
  return (
    <div style={{ 
      whiteSpace: 'pre-wrap', 
      wordBreak: 'break-word', 
      color: 'transparent',
      ...style 
    }}>
      {parts.map((part, i) =>
        /^\[[A-Za-z]+\.[A-Za-z]+\]$/.test(part) ? (
          <span key={i} className="bg-yellow-200/80 rounded-[2px]">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </div>
  );
}
